/* eslint-disable jsdoc/check-param-names, jsdoc/require-param --
 * We don't document each `case` param, instead we document the concept more
 * generally, but these eslint rules can't detect that.
 */

import { purryOn } from "./internal/purryOn";
import type { GuardType } from "./internal/types/GuardType";

type Case<
  In,
  Out,
  When extends (x: In) => boolean = (x: In) => boolean,
> = readonly [when: When, then: (x: GuardType<When, In> & In) => Out];

type DefaultCase<In, Out> = (x: In) => Out;

/**
 * Executes a transformer function based on the first matching predicate,
 * functioning like a series of `if...else if...` statements. It sequentially
 * evaluates each case and, upon finding a truthy predicate, runs the
 * corresponding transformer, and returns, ignoring any further cases, even if
 * they would match.
 *
 * *NOTE*: Some type-predicates may fail to narrow the param type of their
 * transformer; in such cases wrap your type-predicate in an anonymous arrow
 * function: e.g., instead of
 * `conditional(..., [myTypePredicate, myTransformer], ...)`, use
 * `conditional(..., [($) => myTypePredicate($), myTransformer], ...)`.
 *
 * To add a a default, catch-all, case you can provide a single callback
 * function (instead of a 2-tuple) as the last case. This is equivalent to
 * adding a case with a trivial always-true predicate as it's condition (see
 * example).
 *
 * For simpler cases you should also consider using `when` instead.
 *
 * Due to TypeScript's inability to infer the result of negating a type-
 * predicate we can't refine the types used in subsequent cases based on
 * previous conditions. Using a `switch (true)` statement or ternary operators
 * is recommended for more precise type control when such type narrowing is
 * needed.
 *
 * !IMPORTANT! - Unlike similar implementations in Lodash and Ramda, the Remeda
 * implementation **doesn't** implicitly return `undefined` as a fallback when
 * when none of the cases match; and instead **throws** an exception in those
 * cases! You have to explicitly provide a default case, and can use
 * `constant(undefined)` as your last case to replicate that behavior.
 *
 * @param cases - A list of (up to 10) cases. Each case can be either:
 * - A 2-tuple consisting of a predicate (or type-predicate) and a transformer
 *   function that processes the data if the predicate matches.
 * - A single callback function that acts as a default fallback case.
 * @returns The output of the matched transformer. If no cases match, an
 * exception is thrown. The return type is a union of the return types of all
 * provided transformers.
 * @signature
 *   R.conditional(...cases)(data);
 * @example
 *   const nameOrId = 3 as string | number | boolean;
 *
 *   R.pipe(
 *     nameOrId,
 *     R.conditional(
 *       [R.isString, (name) => `Hello ${name}`],
 *       [R.isNumber, (id) => `Hello ID: ${id}`],
 *     ),
 *   ); //=> 'Hello ID: 3' (typed as `string`), can throw!.
 *
 *   R.pipe(
 *     nameOrId,
 *     R.conditional(
 *       [R.isString, (name) => `Hello ${name}`],
 *       [R.isNumber, (id) => `Hello ID: ${id}`],
 *       R.constant(undefined),
 *     ),
 *   ); //=> 'Hello ID: 3' (typed as `string | undefined`), won't throw.
 *
 *   R.pipe(
 *     nameOrId,
 *     R.conditional(
 *       [R.isString, (name) => `Hello ${name}`],
 *       [R.isNumber, (id) => `Hello ID: ${id}`],
 *       (something) => `Hello something (${JSON.stringify(something)})`,
 *     ),
 *   ); //=> 'Hello ID: 3' (typed as `string`), won't throw.
 * @dataLast
 * @category Function
 */
export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Return0,
  Fallback = never,
>(
  // TODO [>2]: TypeScript has problems inferring narrowed types within the case tuples, making it hard to work with this utility. Removing the tuple wrappers solves these issues without impacting the API too much. @see https://github.com/remeda/remeda/issues/847#issuecomment-3057371891
  case0: Case<T, Return0, Fn0>,
  fallback?: DefaultCase<T, Fallback>,
): (data: T) => Return0 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  // TODO [>2]: Exclude previous guarded types from the next functions in the chain. Although this isn't a prefect solution, it's the common approach to compute the "negation" of a type-predicate, and would provide a more comfortable API for bigger chains for cases like discriminated unions. This TODO is at the first occurrence where it is relevant, but it should be applied to **all** subsequent overloads. p.s. don't forget to update the docs too!
  Fn1 extends (x: T) => boolean,
  Return0,
  Return1,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  fallback?: DefaultCase<T, Fallback>,
): (data: T) => Return0 | Return1 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  fallback?: DefaultCase<T, Fallback>,
): (data: T) => Return0 | Return1 | Return2 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  fallback?: DefaultCase<T, Fallback>,
): (data: T) => Return0 | Return1 | Return2 | Return3 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  fallback?: DefaultCase<T, Fallback>,
): (data: T) => Return0 | Return1 | Return2 | Return3 | Return4 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  fallback?: DefaultCase<T, Fallback>,
): (
  data: T,
) => Return0 | Return1 | Return2 | Return3 | Return4 | Return5 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  fallback?: DefaultCase<T, Fallback>,
): (
  data: T,
) =>
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Fn7 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Return7,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  case7: Case<T, Return7, Fn7>,
  fallback?: DefaultCase<T, Fallback>,
): (
  data: T,
) =>
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Return7
  | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Fn7 extends (x: T) => boolean,
  Fn8 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Return7,
  Return8,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  case7: Case<T, Return7, Fn7>,
  case8: Case<T, Return8, Fn8>,
  fallback?: DefaultCase<T, Fallback>,
): (
  data: T,
) =>
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Return7
  | Return8
  | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Fn7 extends (x: T) => boolean,
  Fn8 extends (x: T) => boolean,
  Fn9 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Return7,
  Return8,
  Return9,
  Fallback = never,
>(
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  case7: Case<T, Return7, Fn7>,
  case8: Case<T, Return8, Fn8>,
  case9: Case<T, Return9, Fn9>,
  fallback?: DefaultCase<T, Fallback>,
): (
  data: T,
) =>
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Return7
  | Return8
  | Return9
  | Fallback;

/**
 * Executes a transformer function based on the first matching predicate,
 * functioning like a series of `if...else if...` statements. It sequentially
 * evaluates each case and, upon finding a truthy predicate, runs the
 * corresponding transformer, and returns, ignoring any further cases, even if
 * they would match.
 *
 * *NOTE*: Some type-predicates may fail to narrow the param type of their
 * transformer; in such cases wrap your type-predicate in an anonymous arrow
 * function: e.g., instead of
 * `conditional(..., [myTypePredicate, myTransformer], ...)`, use
 * `conditional(..., [($) => myTypePredicate($), myTransformer], ...)`.
 *
 * To add a a default, catch-all, case you can provide a single callback
 * function (instead of a 2-tuple) as the last case. This is equivalent to
 * adding a case with a trivial always-true predicate as it's condition (see
 * example).
 *
 * For simpler cases you should also consider using `when` instead.
 *
 * Due to TypeScript's inability to infer the result of negating a type-
 * predicate we can't refine the types used in subsequent cases based on
 * previous conditions. Using a `switch (true)` statement or ternary operators
 * is recommended for more precise type control when such type narrowing is
 * needed.
 *
 * !IMPORTANT! - Unlike similar implementations in Lodash and Ramda, the Remeda
 * implementation **doesn't** implicitly return `undefined` as a fallback when
 * when none of the cases match; and instead **throws** an exception in those
 * cases! You have to explicitly provide a default case, and can use
 * `constant(undefined)` as your last case to replicate that behavior.
 *
 * @param data - The input data to be evaluated against the provided cases.
 * @param cases - A list of (up to 10) cases. Each case can be either:
 * - A 2-tuple consisting of a predicate (or type-predicate) and a transformer
 *   function that processes the data if the predicate matches.
 * - A single callback function that acts as a default fallback case.
 * @returns The output of the matched transformer. If no cases match, an
 * exception is thrown. The return type is a union of the return types of all
 * provided transformers.
 * @signature
 *   R.conditional(data, ...cases);
 * @example
 *   const nameOrId = 3 as string | number | boolean;
 *
 *   R.conditional(
 *     nameOrId,
 *     [R.isString, (name) => `Hello ${name}`],
 *     [R.isNumber, (id) => `Hello ID: ${id}`],
 *   ); //=> 'Hello ID: 3' (typed as `string`), can throw!.
 *
 *   R.conditional(
 *     nameOrId,
 *     [R.isString, (name) => `Hello ${name}`],
 *     [R.isNumber, (id) => `Hello ID: ${id}`],
 *     R.constant(undefined),
 *   ); //=> 'Hello ID: 3' (typed as `string | undefined`), won't throw.
 *
 *   R.conditional(
 *     nameOrId,
 *     [R.isString, (name) => `Hello ${name}`],
 *     [R.isNumber, (id) => `Hello ID: ${id}`],
 *     (something) => `Hello something (${JSON.stringify(something)})`,
 *   ); //=> 'Hello ID: 3' (typed as `string`), won't throw.
 * @dataFirst
 * @category Function
 */
export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Return0,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  fallback?: DefaultCase<T, Fallback>,
): Return0 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Return0,
  Return1,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  fallback?: DefaultCase<T, Fallback>,
): Return0 | Return1 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  fallback?: DefaultCase<T, Fallback>,
): Return0 | Return1 | Return2 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  fallback?: DefaultCase<T, Fallback>,
): Return0 | Return1 | Return2 | Return3 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  fallback?: DefaultCase<T, Fallback>,
): Return0 | Return1 | Return2 | Return3 | Return4 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  fallback?: DefaultCase<T, Fallback>,
): Return0 | Return1 | Return2 | Return3 | Return4 | Return5 | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  fallback?: DefaultCase<T, Fallback>,
):
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Fn7 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Return7,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  case7: Case<T, Return7, Fn7>,
  fallback?: DefaultCase<T, Fallback>,
):
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Return7
  | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Fn7 extends (x: T) => boolean,
  Fn8 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Return7,
  Return8,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  case7: Case<T, Return7, Fn7>,
  case8: Case<T, Return8, Fn8>,
  fallback?: DefaultCase<T, Fallback>,
):
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Return7
  | Return8
  | Fallback;

export function conditional<
  T,
  Fn0 extends (x: T) => boolean,
  Fn1 extends (x: T) => boolean,
  Fn2 extends (x: T) => boolean,
  Fn3 extends (x: T) => boolean,
  Fn4 extends (x: T) => boolean,
  Fn5 extends (x: T) => boolean,
  Fn6 extends (x: T) => boolean,
  Fn7 extends (x: T) => boolean,
  Fn8 extends (x: T) => boolean,
  Fn9 extends (x: T) => boolean,
  Return0,
  Return1,
  Return2,
  Return3,
  Return4,
  Return5,
  Return6,
  Return7,
  Return8,
  Return9,
  Fallback = never,
>(
  data: T,
  case0: Case<T, Return0, Fn0>,
  case1: Case<T, Return1, Fn1>,
  case2: Case<T, Return2, Fn2>,
  case3: Case<T, Return3, Fn3>,
  case4: Case<T, Return4, Fn4>,
  case5: Case<T, Return5, Fn5>,
  case6: Case<T, Return6, Fn6>,
  case7: Case<T, Return7, Fn7>,
  case8: Case<T, Return8, Fn8>,
  case9: Case<T, Return9, Fn9>,
  fallback?: DefaultCase<T, Fallback>,
):
  | Return0
  | Return1
  | Return2
  | Return3
  | Return4
  | Return5
  | Return6
  | Return7
  | Return8
  | Return9
  | Fallback;

export function conditional(...args: readonly unknown[]): unknown {
  return purryOn(isCase, conditionalImplementation, args);
}

function conditionalImplementation<In, Out>(
  data: In,
  ...cases: readonly (Case<In, Out> | DefaultCase<In, Out>)[]
): Out {
  for (const current of cases) {
    if (typeof current === "function") {
      return current(data);
    }

    const [when, then] = current;
    if (when(data)) {
      return then(data);
    }
  }

  // TODO [>2]: When we built this function originally we didn't want to have to always return `undefined` and force users to always have to handle that case even when they knew it would never happen. In hindsight that was wrong and we can support this at the type-level without throwing. If users want to throw they can always have an explicit fallback that does that (and we might add a throw utility in v3 too!).
  throw new Error("conditional: data failed for all cases");
}

function isCase(maybeCase: unknown): maybeCase is Case<unknown, unknown> {
  if (!Array.isArray(maybeCase)) {
    return false;
  }

  const [when, then, ...rest] = maybeCase as readonly unknown[];
  return (
    typeof when === "function" &&
    when.length <= 1 &&
    typeof then === "function" &&
    then.length <= 1 &&
    rest.length === 0
  );
}
